import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import type { WeekendGridModalProps } from "./WeekendGridModal.types";
import type { Role, Shift, SolverPhaseNeedDayOptions, WeekendGrid } from "@/types.ts";
import RoleDayGrid from "@/components/sections/RoleDayGrid";
import { createRoleDayUIItem, createRoleHeaderDayUIItem } from "@/utils/roleDayGridUtils";
import { fetchWeekendGrid, updateWeekendGrid } from "@/api.ts";

const DAYS = ["0", "1", "2", "3", "4", "5", "6"];

const headerDays = DAYS.map((d) => createRoleHeaderDayUIItem(d));

const computeDefaultGrid = (roles: Role[], shifts: Shift[]): WeekendGrid => {
    const grid: WeekendGrid = {};
    let anyWeekend = false;

    shifts.forEach((s) => {
        if (s.weekendShift !== true || !s.roleId) {
            return;
        }
        const dayId = String(new Date(s.start).getDay());

        if (!grid[s.roleId]) {
            grid[s.roleId] = {};
        }

        grid[s.roleId][dayId] = true;

        anyWeekend = true;
    });

    if (!anyWeekend) {
        roles.forEach((r) => {
            grid[r.id] = { "0": true, "6": true };
        });
    }

    return grid;
};

const WeekendGridModal: React.FC<WeekendGridModalProps> = ({ open, onClose, roles, shifts }) => {
    const [grid, setGrid] = useState<WeekendGrid>({});
    const [loading, setLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setSaveError(null);

        fetchWeekendGrid()
            .then((data) => {
                if (cancelled) {
                    return;
                }

                const configured = data && Object.keys(data).length > 0;
                setGrid(configured ? data : computeDefaultGrid(roles, shifts));
                setLoading(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setGrid(computeDefaultGrid(roles, shifts));
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    const handleRoleDayClick = (roleId: string, dayId: string) => {
        setGrid((prev) => ({
            ...prev,
            [roleId]: { ...(prev[roleId] ?? {}), [dayId]: !(prev[roleId]?.[dayId] ?? false) },
        }));
    };

    const handleRoleAllDaysClick = (roleId: string) => {
        setGrid((prev) => {
            const row = prev[roleId] ?? {};
            const isAll = DAYS.every((d) => row[d] === true);
            const next: Record<string, boolean> = {};

            DAYS.forEach((d) => {
                next[d] = !isAll;
            });

            return { ...prev, [roleId]: next };
        });
    };

    const listOfUINeeds = roles.map((role) => {
        const daysData: Record<string, SolverPhaseNeedDayOptions> = {};

        Object.entries(grid[role.id] ?? {}).forEach(([dayId, value]) => {
            daysData[dayId] = { apply: value };
        });

        const days = DAYS.map((d) => createRoleDayUIItem(d, daysData));

        return {
            name: role.name,
            id: role.id,
            days,
            isAll: days.every((day) => day.value),
        };
    });

    const handleConfirm = async () => {
        setSaveError(null);
        try {
            await updateWeekendGrid(grid);
            onClose();
        } catch {
            setSaveError("Échec de la sauvegarde. Vérifie ta connexion et réessaie.");
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Quarts de fin de semaine"
            size="xl"
            footer={
                <>
                    <button
                        onClick={onClose}
                        className="inline-flex items-center rounded-full bg-gray-200 cursor-pointer px-4 py-2 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className={`inline-flex items-center rounded-full bg-green-600 px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-green-700"}`}
                    >
                        Confirmer
                    </button>
                </>
            }
        >
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"/>
                    Chargement…
                </div>
            ) : (
                <>
                    <RoleDayGrid
                        headerDays={headerDays}
                        listOfUINeeds={listOfUINeeds}
                        onRoleDayClick={handleRoleDayClick}
                        onRoleAllDaysClick={handleRoleAllDaysClick}
                    />
                    {saveError && (
                        <p className="mt-2 text-sm text-red-600">{saveError}</p>
                    )}
                </>
            )}
        </Modal>
    );
};

export default WeekendGridModal;
